import { useEffect, useRef, useCallback } from 'react';
import { WS_BASE } from '../constants';

const WS_PATH = '/ws/strategies/tier1/trail-updates/';
const RECONNECT_DELAY_MS = 3000;

/**
 * useTier1TrailAlerts
 *
 * Opens a WebSocket to the TIER 1 SL trail-update feed. Calls `onAlert(data)`
 * whenever the backend broadcasts a new SL level (peak_bp moved on a 1m bar).
 *
 * data shape:
 *   {
 *     system_id:          "TIER1",
 *     entry_decision_id:  number,
 *     bar_ts:             ISO string,
 *     peak_bp:            number,
 *     prev_peak_bp:       number,
 *     armed:              boolean,
 *     sl_price:           number,   ← the new stop-loss price
 *     atr_bp:             number,
 *     entry_price:        number,
 *     side:               "long" | "short",
 *   }
 */
export default function useTier1TrailAlerts(onAlert) {
  const wsRef        = useRef(null);
  const timerRef     = useRef(null);
  const onAlertRef   = useRef(onAlert);
  const cancelledRef = useRef(false);

  // Keep the callback ref current without re-creating the WS on every render
  useEffect(() => { onAlertRef.current = onAlert; }, [onAlert]);

  const connect = useCallback(() => {
    if (cancelledRef.current) return;
    try {
      const ws = new WebSocket(`${WS_BASE}${WS_PATH}`);
      wsRef.current = ws;

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data && onAlertRef.current) onAlertRef.current(data);
        } catch {}
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!cancelledRef.current) {
          timerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };

      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    } catch {
      if (!cancelledRef.current) {
        timerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    connect();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        try { wsRef.current.close(); } catch {}
        wsRef.current = null;
      }
    };
  }, [connect]);
}
